import { Stage } from "../../shared/CaseConfiguration";
import { ClaimTypes } from "../../shared/CaseModel";
import { CaseListElement } from "../../shared/CasesList";

/** The agreements a case can be funded by, in the order they are listed. */
export const FUNDING_TYPES = [
  "lawFirmCfa",
  "barristerCfa",
  "thirdPartyFunding",
  "afterTheEventInsurance",
] as const;
export type FundingType = (typeof FUNDING_TYPES)[number];

/** How a case without any of the agreements is named as a filter. */
export const NO_FUNDING = "none";

/** How many of the most recently changed cases the start page lists. */
const RECENT = 5;

/** What the start page says about the cases of a user, from their list alone. */
export interface HomeSummary {
  total: number;
  /** The cases that are not fully settled, partially settled ones among them. */
  open: number;
  partial: number;
  settled: number;
  changedToday: number;
  /** The amounts claimed across all cases. */
  claimed: number;
  /** The most recently changed cases, the latest first. */
  recent: CaseListElement[];
  /** The cases that are not fully settled, by the stage they have reached. */
  stages: { stage: Stage; count: number }[];
  /** How many cases have each claim type, the most frequent first. */
  claimTypes: { type: string; count: number }[];
  /** How many cases have each agreement; a case with several counts for each. */
  funding: { type: FundingType | typeof NO_FUNDING; count: number }[];
}

const changedAt = (c: CaseListElement) =>
  c.updatedAt ? new Date(c.updatedAt).getTime() : 0;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * The cases of a user counted for the start page: nothing here is more than
 * the list of cases holds, counted and put in order.
 */
export function summarise(
  cases: readonly CaseListElement[],
  stages: readonly Stage[],
  now: Date = new Date(),
): HomeSummary {
  const open = cases.filter((c) => c.settlementStatus !== "full");
  const count = (
    list: readonly CaseListElement[],
    test: (c: CaseListElement) => boolean,
  ) => list.filter(test).length;

  const claimTypes = Object.values(ClaimTypes)
    .map((type) => ({
      type: type as string,
      count: count(cases, (c) => type in c.claimTypeCounts),
    }))
    .filter((entry) => entry.count > 0)
    // the most frequent first; the order of the types settles a tie
    .sort((a, b) => b.count - a.count);

  return {
    total: cases.length,
    open: open.length,
    partial: count(cases, (c) => c.settlementStatus === "partial"),
    settled: cases.length - open.length,
    changedToday: count(
      cases,
      (c) => c.updatedAt !== undefined && sameDay(new Date(c.updatedAt), now),
    ),
    claimed: cases.reduce((sum, c) => sum + (c.claimValue ?? 0), 0),
    recent: [...cases]
      .filter((c) => c.updatedAt !== undefined)
      .sort((a, b) => changedAt(b) - changedAt(a))
      .slice(0, RECENT),
    stages: stages.map((stage) => ({
      stage,
      count: count(open, (c) => (c.currentStage ?? Stage.PreAction) === stage),
    })),
    claimTypes,
    funding: [
      ...FUNDING_TYPES.map((type) => ({
        type,
        count: count(cases, (c) => c.agreementTypes.includes(type)),
      })),
      {
        type: NO_FUNDING,
        count: count(
          cases,
          (c) => !FUNDING_TYPES.some((type) => c.agreementTypes.includes(type)),
        ),
      },
    ],
  };
}
